/* eslint-disable no-unused-vars */

import { getApiErrorMessage } from "@core/utility/get-error-message";
import {
  ageRangeReportController,
  campaignReportController,
  cardStatsReportController,
  earnPointsReportController,
  earnStampReportController,
  GetResponseReportCardStat,
  GetResponseReportRank,
  rankReportController,
  redeemPointsReportController,
  redeemStampReportController,
} from "@factory/model/query-model-report";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { AxiosError } from "axios";
import { useQuery } from "react-query";

import { authHeaders } from "../authentication/auth-header";
import { useSessionContext } from "../authentication/session.context";
import { QueryKey } from "./query-key.enum";

export function useQyGetCardStatsReport(
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await cardStatsReportController(
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportCardStatus, dateFilter, startDate, endDate],
    queryFn: () => apiFn(dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetAgeRangeReport(
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await ageRangeReportController(
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportAgeRange, dateFilter, startDate, endDate],
    queryFn: () => apiFn(dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetRankReport(
  limit: number = 999,
  offset: number = 0,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportRank) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    limit?: number,
    offset?: number,
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await rankReportController(
      limit,
      offset,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportRank;
  };

  return useQuery({
    enabled,
    queryKey: [
      QueryKey.ReportRank,
      limit,
      offset,
      dateFilter,
      startDate,
      endDate,
    ],
    queryFn: () => apiFn(limit, offset, dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetCampaignReport(
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await campaignReportController(
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportCampaign, dateFilter, startDate, endDate],
    queryFn: () => apiFn(dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetEarnPointsReport(
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await earnPointsReportController(
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportEarnPoints, dateFilter, startDate, endDate],
    queryFn: () => apiFn(dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetEarnStampReport(
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await earnStampReportController(
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportEarnStamp, dateFilter, startDate, endDate],
    queryFn: () => apiFn(dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetRedeemPointsReport(
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await redeemPointsReportController(
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportRedeemPoints, dateFilter, startDate, endDate],
    queryFn: () => apiFn(dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetRedeemStampReport(
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseReportCardStat) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    dateFilter?: string,
    startDate?: string,
    endDate?: string
  ) => {
    await checkSession();

    showProgress();
    const operation = await redeemStampReportController(
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseReportCardStat;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKey.ReportRedeemStamp, dateFilter, startDate, endDate],
    queryFn: () => apiFn(dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}
